import { decryptData } from "@/features/cryptography";
import { supabaseApi, type PortfolioAsset } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import type { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { hexToArrayBuffer } from "@/utils/crypto";
import { Effect, pipe } from "effect";
import type {
  LightweightResultPerScenario,
  ResponseSuccess,
  ResponseSuccessLightweight,
  ResponseSuccessWithMetadata,
  Scenario,
} from "../schema";
import { transformFullToLightweight } from "../schema";
import type { DBConfig } from "../utils/indexeddb";
import { IndexedDb } from "../utils/indexeddb";
import { lineReducer } from "../utils/line_reducer";
import { streamResults } from "../utils/stream";
import type { CachedAdaptation } from "./AdaptationCacheSchema";
import type { CachedAsset } from "./AssetCacheSchema";
import { assetInputRowCount, isAssetCacheComplete } from "./cacheValidity";
import { ADAPTATIONS_IDB_STORE_NAME, createPortfolioCacheConfig, FULL_RESULT_PAGE_SIZE } from "./portfolioCache";

const BATCH_SIZE = 100;
const CONCURRENCY_LIMIT = 3;
const ASSET_INPUT_CONCURRENCY_LIMIT = 6;
const MAX_RETRIES = 10;
const RETRY_TIMEOUT = 3000;

type AssetInput = {
  id: string;
  /** `portfolio_assets` row id — the identity the baseline adaptation mirror needs. */
  portfolioAssetId: string;
  data: ResponseSuccessWithMetadata["metadata"];
};

/**
 * A cold load runs all three in sequence, and the progress count restarts at
 * each hand-over, so consumers should caption the phase rather than presenting
 * one continuous bar. A warm load runs only `transforming`.
 *
 * - `preparing` — pulling and decrypting the asset inputs from Supabase.
 * - `downloading` — streaming full results for assets missing from the cache.
 * - `transforming` — reading cached results back out of IndexedDB.
 */
export type LoadPhase = "preparing" | "downloading" | "transforming";

/**
 * Progress callback for loading operations
 */
export interface LoadProgress {
  downloaded: number;
  processed: number;
  total: number;
  phase?: LoadPhase;
}

export type OnProgressCallback = (progress: LoadProgress) => void;

/** Progress updates per phase: each one re-renders the page. */
const PROGRESS_UPDATES = 100;

/** Forwards progress once per 1% of the total, so small read pages don't re-render the page on each one. */
export const throttleProgress = (onProgress?: OnProgressCallback): OnProgressCallback | undefined => {
  if (!onProgress) return undefined;
  let last: LoadProgress | undefined;
  return (progress) => {
    const isRoutine =
      last !== undefined &&
      last.phase === progress.phase &&
      progress.processed < progress.total &&
      progress.processed - last.processed < progress.total / PROGRESS_UPDATES;
    if (isRoutine) return;
    last = progress;
    onProgress(progress);
  };
};

/**
 * Process stream of results from facades API
 */
const processStreamToResults = async (
  body: ReadableStream<Uint8Array>,
  metadataFor: (itemId: string) => ResponseSuccessWithMetadata["metadata"] | undefined
): Promise<ResponseSuccessWithMetadata[]> => {
  const results: ResponseSuccessWithMetadata[] = [];
  const reader = body
    .pipeThrough(new TextDecoderStream() as unknown as ReadableWritablePair<string, Uint8Array>)
    .getReader();

  let previousLine = "";
  let chunk = await reader.read();

  while (!chunk.done) {
    const data = chunk.value
      .split("\n")
      .map((line) => (line.startsWith("\x1E") ? line.slice(1) : line))
      .reduce(lineReducer, {
        previousLine,
        successes: [],
        errors: [],
      });

    results.push(
      ...data.successes.map((result: ResponseSuccess) => {
        const metadata = metadataFor(result.item_id);
        if (!metadata) {
          throw new Error("Asset metadata not found for result");
        }

        return {
          ...result,
          metadata,
        };
      })
    );

    previousLine = data.previousLine;
    chunk = await reader.read();
  }

  if (previousLine) {
    try {
      const lastResult = JSON.parse(previousLine);
      const metadata = metadataFor(lastResult.item_id);
      if (metadata) {
        results.push({ ...lastResult, metadata });
      }
    } catch {
      console.warn("Could not parse last line fragment:", previousLine);
    }
  }

  return results;
};

/**
 * Unified asset data loader with IDB caching and concurrent fetching
 *
 * MEMORY OPTIMIZATION STRATEGY:
 * - Caches FULL results in IndexedDB (cheap storage)
 * - Transforms to lightweight on-demand for map views (reduces memory)
 * - Detail views fetch from cache temporarily (discarded after use)
 * - Supports up to 10k assets with minimal memory footprint
 */
export class AssetDataLoader {
  private config: DBConfig;
  private schema: PortfolioExportJobSchema;
  private allAssetInputsPromise: Promise<AssetInput[]> | null = null;
  private assetInputByIdPromise: Promise<ReadonlyMap<string, AssetInput>> | null = null;

  private baselineAdaptationsConfig: DBConfig | null;

  constructor(schema: PortfolioExportJobSchema, canRunAutoAdaptation = false) {
    this.schema = schema;
    this.config = createPortfolioCacheConfig(schema.id);
    this.baselineAdaptationsConfig =
      canRunAutoAdaptation && schema.autoAdaptationStatus !== null
        ? createPortfolioCacheConfig(schema.id, { store: ADAPTATIONS_IDB_STORE_NAME })
        : null;
  }

  /**
   * Fills the cache so every asset is present.
   */
  private async ensureCacheComplete(onProgress?: OnProgressCallback): Promise<void> {
    const totalAssets = this.schema.assets.totalCount;

    // Only the diff against the count
    const cachedCount = await this.getCachedAssetCount();

    if (isAssetCacheComplete({ schema: this.schema, cachedCount })) {
      console.log(`[AssetDataLoader] cache complete (${cachedCount} assets), skipping asset input fetch`);
      return;
    }

    const cachedIds = new Set(await this.getCachedAssetIds());

    const allAssetInputs = await this.getAllAssetInputs((prepared) => {
      onProgress?.({ phase: "preparing", downloaded: 0, processed: prepared, total: totalAssets });
    });
    const assetIdsToFetch = allAssetInputs.map((asset) => asset.id).filter((id) => !cachedIds.has(id));

    console.log(`[AssetDataLoader] ${cachedIds.size}/${totalAssets} assets cached, fetching ${assetIdsToFetch.length}`);

    if (assetIdsToFetch.length > 0) {
      let downloaded = 0;
      await this.fetchAndCacheFull(assetIdsToFetch, (count) => {
        downloaded += count;
        onProgress?.({
          phase: "downloading",
          downloaded,
          processed: Math.min(cachedIds.size + downloaded, totalAssets),
          total: totalAssets,
        });
      });
    }
  }

  /**
   * Load lightweight results for map view - transforms from cached full results
   * Only fetches from API if not in cache
   */
  async loadForMapView(
    scenario: Scenario = "rcp85",
    onLoadProgress?: OnProgressCallback
  ): Promise<ResponseSuccessLightweight[]> {
    const onProgress = throttleProgress(onLoadProgress);
    const totalAssets = this.schema.assets.totalCount;
    let downloaded = 0;

    await this.ensureCacheComplete(onProgress);

    const lightweightResults: ResponseSuccessLightweight[] = [];
    const corruptCacheAssetIds: string[] = [];

    // Recovery runs after the transform pass has already counted the store, so
    // continue from there rather than restarting the count at the refetch.
    const reportDownload = (count: number) => {
      downloaded += count;
      onProgress?.({
        phase: "downloading",
        downloaded,
        processed: Math.min(lightweightResults.length + downloaded, totalAssets),
        total: totalAssets,
      });
    };

    // Transform to lightweight in batches so only one batch of full results
    // (all scenarios) is in memory at a time
    await IndexedDb.getAllItemsBatched<CachedAsset>(
      { ...this.config },
      async (batch) => {
        batch.forEach((cached) => {
          try {
            lightweightResults.push(transformFullToLightweight(cached.full, scenario));
          } catch {
            corruptCacheAssetIds.push(cached.id);
          }
        });
        // On a warm cache this pass is the whole load, so it drives the progress
        onProgress?.({
          phase: "transforming",
          downloaded,
          processed: Math.min(lightweightResults.length, totalAssets),
          total: totalAssets,
        });
      },
      FULL_RESULT_PAGE_SIZE
    );

    if (corruptCacheAssetIds.length > 0) {
      const recovered = await this.recoverCorruptAssets(corruptCacheAssetIds, scenario, reportDownload);
      lightweightResults.push(...recovered);
    }

    onProgress?.({ phase: "transforming", downloaded, processed: totalAssets, total: totalAssets });

    return lightweightResults;
  }

  /**
   * Remove and recover corrupt cached entries that failed to transform.
   */
  private async recoverCorruptAssets(
    corruptIds: string[],
    scenario: Scenario,
    onDownloaded: (count: number) => void
  ): Promise<ResponseSuccessLightweight[]> {
    const idbConfig = { ...this.config };

    console.warn(`[AssetDataLoader] Recovering ${corruptIds.length} corrupt cache assets`);

    // delete corrupt ids
    await Promise.all(corruptIds.map((id) => IndexedDb.deleteItem(idbConfig, id)));

    await this.fetchAndCacheFull(corruptIds, onDownloaded);

    const refetched = await IndexedDb.getItemsByIds<CachedAsset>(idbConfig, corruptIds);

    const recovered: ResponseSuccessLightweight[] = [];
    const corruptAfterRefetch: string[] = [];

    refetched.forEach((cached) => {
      try {
        recovered.push(transformFullToLightweight(cached.full, scenario));
      } catch {
        corruptAfterRefetch.push(cached.id);
      }
    });

    if (corruptAfterRefetch.length > 0) {
      throw new Error(
        `[AssetDataLoader] ${corruptAfterRefetch.length} still assets corrupt after delete and re-fetch.`
      );
    }

    return recovered;
  }

  /**
   * Load one scenario's lightweight slice per asset, keyed by asset id — the
   * only part a scenario change merges, so nothing else is held while it loads.
   */
  async loadForScenario(
    assetIds: string[],
    scenario: Scenario,
    onLoadProgress?: OnProgressCallback
  ): Promise<Map<string, LightweightResultPerScenario>> {
    const onProgress = throttleProgress(onLoadProgress);
    const idbConfig = { ...this.config };
    const idSet = new Set(assetIds);
    const total = assetIds.length;
    const scenarioResults = new Map<string, LightweightResultPerScenario>();

    // read assets in batches to avoids memory failure
    await IndexedDb.getAllItemsBatched<CachedAsset>(
      idbConfig,
      async (batch) => {
        batch.forEach((cached) => {
          if (!idSet.has(cached.id)) return;
          const scenarioResult = transformFullToLightweight(cached.full, scenario).result.results[scenario];
          if (scenarioResult) scenarioResults.set(cached.id, scenarioResult);
        });
        onProgress?.({
          downloaded: scenarioResults.size, // no download, but surfaces cache loading progress visible in large dataset
          processed: Math.min(scenarioResults.size, total),
          total,
        });
      },
      FULL_RESULT_PAGE_SIZE
    );

    onProgress?.({ downloaded: scenarioResults.size, processed: total, total });

    return scenarioResults;
  }

  /**
   * For report payloads that need several scenarios at once.
   */
  async loadForReport<T>(
    assetIds: string[],
    project: (full: ResponseSuccessWithMetadata) => T,
    onProgress?: OnProgressCallback
  ): Promise<T[]> {
    const idbConfig = { ...this.config };
    const total = assetIds.length;
    const projected: T[] = [];

    for (let i = 0; i < total; i += BATCH_SIZE) {
      const cachedBatch = await IndexedDb.getItemsByIds<CachedAsset>(idbConfig, assetIds.slice(i, i + BATCH_SIZE));

      cachedBatch.forEach((cached) => {
        projected.push(project(cached.full));
      });

      onProgress?.({
        downloaded: projected.length, // no download, but surfaces cache loading progress visible in large selections
        processed: Math.min(i + BATCH_SIZE, total),
        total,
      });
    }

    onProgress?.({ downloaded: projected.length, processed: total, total });

    return projected;
  }

  /**
   * Asset ids in upload order. The cache reads back in key order, so a caller that needs the
   * portfolio's own order sorts by this.
   */
  async loadAssetOrder(): Promise<string[]> {
    return (await this.getAllAssetInputs()).map((asset) => asset.id);
  }

  /**
   * Get full result for a single asset - fetched from cache temporarily
   * For detail views - should be discarded after use to save memory
   */
  async loadFullResult(assetId: string): Promise<ResponseSuccessWithMetadata | null> {
    const idbConfig = { ...this.config };

    // Check cache first
    const cached = await IndexedDb.getItem<CachedAsset>(idbConfig, assetId);
    if (cached?.full) {
      console.log(`[AssetDataLoader] Full result for ${assetId} found in cache`);
      return cached.full;
    }

    console.log(`[AssetDataLoader] Fetching full result for ${assetId}`);

    // Fetch from API if not cached
    const results = await this.fetchFullResults([assetId]);
    const result = results[0];

    if (!result) {
      return null;
    }

    // Cache the full result
    const newCache: CachedAsset = {
      id: assetId,
      full: result,
      timestamp: Date.now(),
    };

    await IndexedDb.upsertItems(idbConfig, [newCache]);

    return result;
  }

  /**
   * Load full results for download (checks cache first)
   */
  async loadForDownload(onProgress?: OnProgressCallback): Promise<ResponseSuccessWithMetadata[]> {
    const totalAssets = this.schema.assets.totalCount;

    await this.ensureCacheComplete(onProgress);

    // The download contract needs every full result in one array; the batched
    // read at least avoids holding a second copy of the store alongside it
    const allResults: ResponseSuccessWithMetadata[] = [];
    await IndexedDb.getAllItemsBatched<CachedAsset>({ ...this.config }, async (batch) => {
      batch.forEach((cached) => allResults.push(cached.full));
      // Nothing is downloaded here — this reads back what ensureCacheComplete
      // already fetched — so only `processed` advances.
      onProgress?.({
        phase: "transforming",
        downloaded: 0,
        processed: Math.min(allResults.length, totalAssets),
        total: totalAssets,
      });
    });

    onProgress?.({ phase: "transforming", downloaded: 0, processed: totalAssets, total: totalAssets });

    return allResults;
  }

  /**
   * Clear all cached data for this portfolio
   */
  async clearCache(): Promise<void> {
    const idbConfig = { ...this.config };
    await IndexedDb.clearStore(idbConfig);
    await IndexedDb.closeDatabase(idbConfig);
    // Optional: callers can explicitly delete DB via IndexedDb.deleteDatabase if desired.
  }

  /**
   * Private: Get the ids of all cached assets without loading their values
   */
  private async getCachedAssetIds(): Promise<string[]> {
    const idbConfig = { ...this.config };
    try {
      return (await IndexedDb.getAllKeys(idbConfig)) as string[];
    } catch {
      return [];
    }
  }

  /**
   * Private: How many assets are cached, without materialising their ids.
   *
   * A warm load only needs the number, and at 20k assets pulling the keys
   * across the structured-clone boundary to then call `.size` on them is the
   * bulk of the work it does.
   */
  private async getCachedAssetCount(): Promise<number> {
    try {
      const { count } = await IndexedDb.getCount({ ...this.config });
      return count;
    } catch {
      return 0;
    }
  }

  /**
   * Private: Pull encrypted asset metadata from Supabase and decrypt
   */
  private async pullAssetInputs(offset: number, length: number): Promise<AssetInput[]> {
    const action = supabaseApi.endpoints.getSuccessfulPortfolioAssets.initiate({
      portfolioId: this.schema.id,
      start: offset,
      length,
    });

    const data = await dispatchWithRetries<PortfolioAsset[]>({
      action,
      maxRetries: MAX_RETRIES,
      timeout: RETRY_TIMEOUT,
    });

    return Promise.all(
      data.map(async (asset) => {
        if (!asset.data) {
          throw new Error("Missing encrypted asset data");
        }
        const decryptedData = await decryptData(this.schema.secret, hexToArrayBuffer(asset.data));
        const decryptedId = await decryptData(this.schema.secret, hexToArrayBuffer(asset.encrypted_id));

        return {
          id: decryptedId,
          portfolioAssetId: asset.id,
          data: JSON.parse(decryptedData) as ResponseSuccessWithMetadata["metadata"],
        };
      })
    );
  }

  /**
   * Private: Pull all asset inputs, memoised per loader instance.
   *
   * Batches run concurrently rather than each waiting on the last — the asset inputs
   * is a hard serial prefix before a single result can start downloading, so
   * this latency is paid before anything renders. `onLoaded` reports the running
   * total; because the result is memoised it only fires for the first caller,
   * which is the one that actually waits.
   */
  private async getAllAssetInputs(onLoaded?: (loaded: number) => void): Promise<AssetInput[]> {
    if (!this.allAssetInputsPromise) {
      this.allAssetInputsPromise = (async () => {
        // The asset input query filters out errored assets, so paging to
        // `totalCount` would spend the tail of the run on empty responses.
        const assetInputRows = assetInputRowCount(this.schema);
        const offsets: number[] = [];
        for (let i = 0; i < assetInputRows; i += BATCH_SIZE) {
          offsets.push(i);
        }

        let loaded = 0;
        const pullBatch = (offset: number) =>
          Effect.tryPromise({
            try: async () => {
              const batch = await this.pullAssetInputs(offset, Math.min(BATCH_SIZE, assetInputRows - offset));
              loaded += batch.length;
              onLoaded?.(loaded);
              return batch;
            },
            // Keep the underlying Supabase error rather than wrapping it in a
            // defect, so the failure that reaches Sentry is the real one.
            catch: (error) => (error instanceof Error ? error : new Error(String(error))),
          });

        // Effect.forEach preserves input order, so the asset inputs stay stable.
        const batches = await Effect.runPromise(
          Effect.forEach(offsets, pullBatch, { concurrency: ASSET_INPUT_CONCURRENCY_LIMIT })
        );
        return batches.flat();
      })();
    }
    return this.allAssetInputsPromise;
  }

  /** Private: the asset inputs indexed by analysis id, built once per loader. */
  private async getAssetInputById(): Promise<ReadonlyMap<string, AssetInput>> {
    if (!this.assetInputByIdPromise) {
      this.assetInputByIdPromise = this.getAllAssetInputs().then((all) => new Map(all.map((a) => [a.id, a] as const)));
    }
    return this.assetInputByIdPromise;
  }

  /**
   * Private: Fetch full results and cache them
   */
  private async fetchAndCacheFull(assetIds: string[], onDownloaded?: (count: number) => void): Promise<void> {
    const idbConfig = { ...this.config };

    // Process in batches with concurrency using Effect
    const batches: string[][] = [];
    for (let i = 0; i < assetIds.length; i += BATCH_SIZE) {
      batches.push(assetIds.slice(i, i + BATCH_SIZE));
    }

    const fetchBatch = (batchIds: string[]) =>
      pipe(
        Effect.promise(async () => {
          const fullResults = await this.fetchFullResults(batchIds);

          // Cache full results
          const toCache: CachedAsset[] = fullResults.map((result) => ({
            id: result.item_id,
            full: result,
            timestamp: Date.now(),
          }));

          await IndexedDb.upsertItems(idbConfig, toCache);

          if (this.baselineAdaptationsConfig) {
            try {
              // The baseline adaptation re-runs the asset inputs unchanged, so
              // its actions share the asset's analysis id and never stream
              // separately. Mirroring the results under the identity
              // AdaptationsLoader expects is what keeps it off the network.
              await IndexedDb.upsertItems(this.baselineAdaptationsConfig, await this.toBaselineAdaptations(toCache));
            } catch (e) {
              console.warn("[AssetDataLoader] baseline adaptation mirror failed", e);
            }
          }

          onDownloaded?.(batchIds.length);
        })
      );

    const batchEffects = Effect.forEach(batches, fetchBatch, { concurrency: CONCURRENCY_LIMIT, discard: true });
    await Effect.runPromise(batchEffects);
  }

  /**
   * Private: Re-key cached assets as the baseline adaptation records that
   * `AdaptationsLoader` reads back.
   */
  private async toBaselineAdaptations(cached: CachedAsset[]): Promise<CachedAdaptation[]> {
    const assetInputById = await this.getAssetInputById();

    return cached.reduce<CachedAdaptation[]>((acc, asset) => {
      const input = assetInputById.get(asset.id);
      if (!input) return acc;
      acc.push({ ...asset, portfolio_asset_id: input.portfolioAssetId, adaptation_type: "baseline" });
      return acc;
    }, []);
  }

  /**
   * Private: Fetch full results from API
   */
  private async fetchFullResults(assetIds: string[]): Promise<ResponseSuccessWithMetadata[]> {
    const assetInputById = await this.getAssetInputById();

    // Stream results from facades
    const response = await streamResults(assetIds, this.schema.stamps, MAX_RETRIES, RETRY_TIMEOUT);

    if (!response.body) {
      throw new Error("No response body received from stream");
    }

    const results = await processStreamToResults(response.body, (itemId) => assetInputById.get(itemId)?.data);
    return results;
  }
}
